import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import type { AppConfig, Uniforms } from './interfaces';
import vertexShader from './shaders/vertex.glsl?raw';
import fragmentShader from './shaders/fragment.glsl?raw';

export class SceneManager {
  private config: AppConfig;
  public renderer: THREE.WebGLRenderer;
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  public uniforms: Uniforms;
  private bloomComposer: EffectComposer;
  private mesh: THREE.Mesh;

  constructor(config: AppConfig) {
    this.config = config;
    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    document.body.appendChild(this.renderer.domElement);

    // Scene and Camera
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.set(this.config.cameraPosX, this.config.cameraPosY, this.config.cameraPosZ);
    this.camera.lookAt(this.config.cameraLookAtX, this.config.cameraLookAtY, this.config.cameraLookAtZ);

    // Add Fog
    if (this.config.fogColor !== undefined && this.config.fogNearMin !== undefined && this.config.fogFarMin !== undefined) {
      this.scene.fog = new THREE.Fog(this.config.fogColor, this.config.fogNearMin, this.config.fogFarMin);
      // You can also set the background color to match the fog for a seamless effect
      // this.scene.background = new THREE.Color(this.config.fogColor);
    }

    // Uniforms
    this.uniforms = {
      u_time: { value: 0.0 },
      u_frequency: { value: 0.0 },
      u_red: { value: this.config.red },
      u_green: { value: this.config.green },
      u_blue: { value: this.config.blue },
      fogColor: { value: new THREE.Color(this.config.fogColor || 0x000000) },
      fogNear: { value: this.config.fogNearMin || 0.1 },
      fogFar: { value: this.config.fogFarMin || 1000 } 
    };

    // Material and Geometry
    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader,
      fragmentShader,
      fog: true
    });
    const geo = new THREE.IcosahedronGeometry(this.config.icosahedronRadius, this.config.icosahedronDetail);
    this.mesh = new THREE.Mesh(geo, mat);
    (this.mesh.material as THREE.ShaderMaterial).wireframe = true;
    this.scene.add(this.mesh);

    // Post-processing
    const renderScenePass = new RenderPass(this.scene, this.camera);
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      this.config.bloomStrength,
      this.config.bloomRadius,
      this.config.bloomThreshold
    );

    this.bloomComposer = new EffectComposer(this.renderer);
    this.bloomComposer.addPass(renderScenePass);
    this.bloomComposer.addPass(bloomPass);
    const outputPass = new OutputPass();
    this.bloomComposer.addPass(outputPass);

    window.addEventListener("resize", this.handleResize.bind(this));
  }

  public render(): void {
    this.bloomComposer.render();
  }

  private handleResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.bloomComposer.setSize(window.innerWidth, window.innerHeight);
  }
}